import { Component, Input } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { Tradable } from "@models/interfaces/tradable";
import { FeaturedAssetsService } from "@main-services/featured-assets.service";
import { Router } from "@angular/router";
import { FEATURED_ASSET_LIST_SLICE } from "@helpers/constants";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PathResolverService } from "@main-services/path-resolver.service";
import { isDigiAsset } from "@modules/trade/helpers/helper";
import { SegmentService } from "@main-services/segment.service";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Component({
	selector: "featured-assets",
	templateUrl: "./featured-assets.component.html",
	styleUrls: ["./featured-assets.component.scss"],
	standalone: false
})
export class FeaturedAssetsComponent {
	private readonly FEATURED_ASSET_CLICKED = "Featured Asset Card Clicked";
	private readonly FEATURED_ASSET_LOCATION = "Asset Detail Page";

	@Input()
	public set tradable(item: Tradable) {
		if (!this._tradable || item.getId() !== this._tradable.getId()) {
			this._tradable = item;
			this.setFeaturedAssetList(this.featuredAssetsService.getListSubject().getValue());
		}
	}

	public get tradable(): Tradable {
		return this._tradable;
	}

	private featuredAssetList: BehaviorSubject<Tradable[]> = new BehaviorSubject([]);
	private _tradable: Tradable = null;

	public constructor(
		private featuredAssetsService: FeaturedAssetsService,
		private router: Router,
		private readonly pathResolverService: PathResolverService,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService
	) {
		this.featuredAssetsService
			.getListSubject()
			.pipe(untilDestroyed(this))
			.subscribe((list: Tradable[]) => {
				this.setFeaturedAssetList(list);
			});
	}

	private setFeaturedAssetList(list: Tradable[]): void {
		if (!this.tradable) {
			return;
		}
		const assetList: Tradable[] = list.filter(
			(tradable) => tradable.getId() !== this.tradable.getId() && !isDigiAsset(tradable.getCurrencyType())
		);
		this.featuredAssetList.next(assetList.slice(0, FEATURED_ASSET_LIST_SLICE));
	}

	public getTradables(): Observable<Tradable[]> {
		return this.featuredAssetList.asObservable();
	}

	public isChangePositive(t: Tradable): boolean {
		return t.getChange24h() > 0;
	}

	public isChangeNegative(t: Tradable): boolean {
		return t.getChange24h() < 0;
	}

	public getAbsoluteChange(tradable: Tradable): number {
		return Math.abs(tradable.getChange24h());
	}

	public onClick(t: Tradable): void {
		this.rudderstackTrack(this.FEATURED_ASSET_CLICKED, this.FEATURED_ASSET_LOCATION, t.getSymbol());
		this.router.navigateByUrl(this.pathResolverService.assetDetails(t));
	}

	private rudderstackTrack(event: string, location: string, symbol: string): void {
		this.segmentService.track({
			name: event,
			properties: {
				screen_it_happened_on: location,
				asset_symbol: symbol
			}
		});
		this.rudderstackService.track({
			name: event,
			properties: {
				screen_it_happened_on: location,
				asset_symbol: symbol
			}
		});
	}
}
